import {type ReactNode, useMemo, useState} from 'react'
import {useEventCallback} from 'usehooks-ts'

import {StepCongrats} from '#/screens/SignUpWizard/StepCongrats'
import {StepInstallWid} from '#/screens/SignUpWizard/StepInstallWid'
import {StepVerifying} from '#/screens/SignUpWizard/StepVerifying'
import {StepVerifyingMobile} from '#/screens/SignUpWizard/StepVerifyingMobile'
import {StepWidSetup} from '#/screens/SignUpWizard/StepWidSetup'
import {StepWidSetupMobile} from '#/screens/SignUpWizard/StepWidSetupMobile'
import {useWidVerificationAutoAdvance} from '#/screens/SignUpWizard/useWidVerificationAutoAdvance'
import {useIsMobileWeb} from '#/alf'
import {useGeolocationServiceResponse} from '#/geolocation/service'
import {useSemanticTokens} from '#/wsocial/service/design-system'

interface WIDUpgradeModalProps {
  onClose: () => void
}

type StepType = 'installWid' | 'widSetup' | 'verifying' | 'congrats'

export function WidLockScreenByAge({onClose}: WIDUpgradeModalProps) {
  const isMobileWeb = useIsMobileWeb()
  const geoResponse = useGeolocationServiceResponse()
  const geoRestricted = geoResponse?.requiredValidation ?? false
  const [step, setStep] = useState<StepType>('installWid')
  const go = useEventCallback((next: StepType) => setStep(next))

  useWidVerificationAutoAdvance({
    step,
    onScanned: () => go('verifying'),
    onVerified: () => go('congrats'),
  })

  const Element = useMemo(() => {
    switch (step) {
      case 'installWid':
        return (
          <StepInstallWid
            geoRestricted={geoRestricted}
            onBack={onClose}
            onSkip={geoRestricted ? undefined : onClose}
            onNext={() => go('widSetup')}
          />
        )
      case 'widSetup':
        if (isMobileWeb) {
          return (
            <StepWidSetupMobile
              onBack={() => go('installWid')}
              onSkip={geoRestricted ? undefined : onClose}
            />
          )
        }
        return (
          <StepWidSetup
            onBack={() => go('installWid')}
            onSkip={geoRestricted ? undefined : onClose}
          />
        )
      case 'verifying':
        if (isMobileWeb) {
          return (
            <StepVerifyingMobile onSkip={onClose} allowSkip={!geoRestricted} />
          )
        }
        return <StepVerifying onSkip={onClose} allowSkip={!geoRestricted} />
      case 'congrats':
        return <StepCongrats onSkip={onClose} onNext={onClose} />
    }
  }, [step, onClose, isMobileWeb, geoRestricted, go])

  return <Overlay>{Element}</Overlay>
}

function Overlay({children}: {children: ReactNode}) {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: tokens.onboarding.bg,
        overflowY: 'auto',
      }}>
      {children}
    </div>
  )
}
